import React, { useId } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import {
  suggestionSectionLabel,
  type ComposeRecipientSuggestion,
  type FilteredComposeRecipientSuggestions,
} from '../utils/composeRecipientSuggestions';

export type RecipientSuggestionListProps = {
  filtered: FilteredComposeRecipientSuggestions;
  highlightedIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (item: ComposeRecipientSuggestion) => void;
  listId?: string;
};

function optionId(listId: string, index: number): string {
  return `${listId}-opt-${index}`;
}

/**
 * Grouped Recent / Address book typeahead for To/Cc/Bcc fields.
 */
const RecipientSuggestionList: React.FC<RecipientSuggestionListProps> = ({
  filtered,
  highlightedIndex,
  onHighlight,
  onSelect,
  listId: listIdProp,
}) => {
  const { colors } = useTheme();
  const reactId = useId();
  const listId = listIdProp ?? `recipient-suggestions-${reactId}`;

  if (filtered.flat.length === 0) return null;

  const sections: Array<{
    source: 'recent' | 'addressBook';
    items: ComposeRecipientSuggestion[];
  }> = [];
  if (filtered.recent.length > 0) {
    sections.push({ source: 'recent', items: filtered.recent });
  }
  if (filtered.addressBook.length > 0) {
    sections.push({ source: 'addressBook', items: filtered.addressBook });
  }

  let flatIndex = 0;

  return (
    <ul
      id={listId}
      role="listbox"
      aria-label="Recipient suggestions"
      className={cn(
        'absolute left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto rounded-lg border py-1 shadow-lg',
        'bg-white dark:bg-gray-900',
        colors.border,
      )}
      style={zStyle(Z_INDEX.portaledDropdown)}
      onMouseDown={(e) => {
        // Keep input focused so selection click is not lost to blur.
        e.preventDefault();
      }}
    >
      {sections.map((section) => (
        <li key={section.source} role="presentation">
          <div
            className={cn(
              'px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider',
              colors.textMuted,
            )}
          >
            {suggestionSectionLabel(section.source)}
          </div>
          <ul role="group" aria-label={suggestionSectionLabel(section.source)}>
            {section.items.map((item) => {
              const index = flatIndex;
              flatIndex += 1;
              const active = index === highlightedIndex;
              const primary = item.name?.trim() || item.email;
              const showSecondary = Boolean(item.name?.trim());
              const id = optionId(listId, index);
              return (
                <li key={`${item.source}-${item.email}`} role="presentation">
                  <button
                    type="button"
                    id={id}
                    role="option"
                    aria-selected={active}
                    className={cn(
                      'flex w-full flex-col items-start gap-0 px-3 py-1.5 text-left transition-colors',
                      active
                        ? 'bg-gray-100 dark:bg-gray-800'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/70',
                    )}
                    onMouseEnter={() => onHighlight(index)}
                    onClick={() => onSelect(item)}
                  >
                    <span className={cn('truncate text-sm font-medium w-full', colors.text)}>
                      {primary}
                    </span>
                    {showSecondary && (
                      <span className={cn('truncate text-xs w-full', colors.textMuted)}>
                        {item.email}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ul>
  );
};

export function recipientSuggestionOptionId(listId: string, index: number): string {
  return optionId(listId, index);
}

export default RecipientSuggestionList;
